import React, { useState } from 'react';
import { Modal, XFilledIcon, Button, Grid, Field, Badge } from 'react-unity';
import OnPremPortOpeningObject from '../../../models/entities/OnPremPortOpeningObject';
import ObjectLookup from '../../common/form-controls/Lookups/ObjectLookup';
import { RuleOperation } from '../../../models/enums/POR/RuleOperation';
import './ImplementedRulesModal.css';

interface ObjectLookupModalProps {
	onSuccess: (objects: OnPremPortOpeningObject[]) => any;
	onCancel: (...args: any[]) => any;
	visible: boolean;
	alreadySelected: string[];
}

const ObjectLookupModal = ({ onSuccess, onCancel, visible, alreadySelected }: ObjectLookupModalProps) => {
	const [selectedObjects, setSelectedObjects] = useState<OnPremPortOpeningObject[]>([]);

	const handleObjectSelection = (obj: OnPremPortOpeningObject | null) => {
		if (obj && !selectedObjects.find((selected) => selected.id === obj.id) && !alreadySelected.includes(obj.name)) {
			setSelectedObjects([...selectedObjects, obj]);
		}
	};

	const handleRemoveObject = (objectId: number) => {
		setSelectedObjects(selectedObjects.filter((obj) => obj.id !== objectId));
	};

	const handleOnSuccess = () => {
		const objects = selectedObjects.map((obj) => ({
			...obj,
			operation: RuleOperation.Modify,
			azureName: obj.name,
		}));
		setSelectedObjects([]);
		onSuccess(objects);
	};

	const handleOnCancel = () => {
		setSelectedObjects([]);
		onCancel();
	};

	const isObjectAlreadySelected = (obj: OnPremPortOpeningObject): boolean => {
		return alreadySelected.includes(obj.name) || selectedObjects.some((selected) => selected.id === obj.id);
	};

	return (
		<Modal show={visible}>
			<Modal.Window className="autoOverflowModal">
				<Modal.Window.Header>
					<Modal.Window.Header.Title>Add objects to the request</Modal.Window.Header.Title>
					<Modal.Window.Header.CloseButton onClick={handleOnCancel}>
						<XFilledIcon size="small" />
					</Modal.Window.Header.CloseButton>
				</Modal.Window.Header>
				<Modal.Window.Body>
					<Field>
						<Field.Body>
							<ObjectLookup
								label="Search for objects"
								placeholder="Search by object name..."
								value={null}
								onChange={handleObjectSelection}
								note="Type at least 3 characters to search for existing objects by name."
							/>
						</Field.Body>
					</Field>

					{selectedObjects.length > 0 && (
						<Field>
							<Field.Body>
								<h4>Selected Objects ({selectedObjects.length})</h4>
								{selectedObjects.map((obj) => (
									<div
										key={obj.id}
										style={{
											padding: '10px',
											border: '1px solid #ddd',
											borderRadius: '4px',
											marginBottom: '8px',
											display: 'flex',
											justifyContent: 'space-between',
											alignItems: 'flex-start',
										}}
									>
										<div style={{ flex: 1 }}>
											<strong>{obj.name}</strong>
											{obj.objectIPs && (
												<div>
													<small>
														<strong>IPs:</strong> {obj.objectIPs.substring(0, 50)}
														{obj.objectIPs.length > 50 ? '...' : ''}
													</small>
												</div>
											)}
											{obj.objectURLsFQDNs && (
												<div>
													<small>
														<strong>URLs:</strong> {obj.objectURLsFQDNs.substring(0, 50)}
														{obj.objectURLsFQDNs.length > 50 ? '...' : ''}
													</small>
												</div>
											)}
											{obj.description && obj.description !== '-' && (
												<div>
													<small>
														<strong>Description:</strong> {obj.description}
													</small>
												</div>
											)}
										</div>
										<Button
											variant="secondary"
											size="small"
											color="negative"
											onClick={() => handleRemoveObject(obj.id)}
										>
											Remove
										</Button>
									</div>
								))}
							</Field.Body>
						</Field>
					)}
				</Modal.Window.Body>
				<Modal.Window.Footer>
					<Grid variant="halves">
						<Grid.Item>
							<Button variant="secondary" onClick={handleOnCancel}>
								Cancel
							</Button>
						</Grid.Item>
						<Grid.Item>
							<Button variant="primary" onClick={handleOnSuccess} disabled={selectedObjects.length === 0}>
								Add Objects ({selectedObjects.length})
							</Button>
						</Grid.Item>
					</Grid>
				</Modal.Window.Footer>
			</Modal.Window>
		</Modal>
	);
};

export default ObjectLookupModal;
